import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Star, Edit, Trash2, Search, X } from 'lucide-react';
//import { db } from '../../firebase/firebaseConfig'; // Assuming this path is correctly
import { db, functions, auth, app } from '../../firebase';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import Bookmark from './Bookmark'; // Import the Bookmark component
import {
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
} from "@material-tailwind/react"; // Added import


const iconPaths = {
  'bookmark': <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>,
  'star': <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>,
  'heart': <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>,
  'file-text': <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></>,
  'message-square': <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>,
  'lightbulb': <path d="M9 18h6M10 22h4M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8A6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"></path>,
  'check-circle': <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></>,
  'alert-circle': <><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></>,
};

// Dummy Bookmark Item component for now
const SortableBookmarkItem = ({ id, bookmark, onToggleFavorite, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 'auto',
    opacity: isDragging ? 0.8 : 1,
  };

  // Example icon rendering logic similar to BookmarkFavorites
  const renderIcon = (icon) => {
    if (!icon) return <div className="w-8 h-8 rounded-full bg-zinc-700"></div>; // Placeholder
    return (
      <div className={`p-2 rounded-full ${icon.bgColor || 'bg-sky-500'} text-white flex-shrink-0`}>
        {/* Basic SVG Placeholder - Replace with dynamic SVGs like in BookmarkFavorites if needed */}
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {/* Use object lookup for the icon path, fallback to bookmark icon if name not found */}
          {iconPaths[icon.name]}
        </svg>
      </div>
    );
  };


  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center bg-zinc-800/60 hover:bg-zinc-700/60 p-3 rounded-lg space-x-3 mb-2 touch-none"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab p-1 text-zinc-500 hover:text-zinc-300 "
        aria-label="Drag to reorder"
      >
        <GripVertical size={18} />
      </button>
      {renderIcon(bookmark.icon)}
      <span className="text-zinc-100 text-sm truncate flex-grow ">{bookmark.name}</span>
      <button
        onClick={() => onToggleFavorite(bookmark.id, !bookmark.favorite)}
        className={`p-2 rounded-full hover:bg-zinc-600 ${bookmark.favorite ? 'text-yellow-400' : 'text-zinc-500 hover:text-yellow-400'}`}
        aria-label={bookmark.favorite ? 'Remove from favorites' : 'Add to favorites'}
      >
        <Star size={18} fill={bookmark.favorite ? 'currentColor' : 'none'} />
      </button>
      <button
        onClick={() => onEdit(bookmark)}
        className="p-2 rounded-full text-zinc-500 hover:text-sky-400 hover:bg-zinc-600"
        aria-label="Edit bookmark"
      >
        <Edit size={18} />
      </button>
      <button
        onClick={() => onDelete(bookmark.id)}
        className="p-2 rounded-full text-zinc-500 hover:text-red-500 hover:bg-zinc-600"
        aria-label="Delete bookmark"
      >
        <Trash2 size={18} />
      </button>
    </div>
  );
};

// New Repeatable Component for displaying the bookmark list
const BookmarkListDisplay = ({
  isLoading,
  sensors,
  handleDragEnd,
  filteredBookmarkIds,
  filteredBookmarks,
  handleToggleFavorite,
  handleEditItem,
  handleDeleteItem,
}) => {
  // DndContext, SortableContext, SortableBookmarkItem, closestCenter, verticalListSortingStrategy
  // are assumed to be in scope from the parent file (BookmarkManager.jsx)
  return (
    <div className="flex-grow overflow-y-auto p-4">
      {isLoading ? (
        <div className="text-center text-zinc-500 py-6">Loading...</div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={filteredBookmarkIds}
            strategy={verticalListSortingStrategy}
          >
            {filteredBookmarks.length > 0 ? (
              filteredBookmarks.map(bookmark => (
                <SortableBookmarkItem
                  key={bookmark.id}
                  id={bookmark.id}
                  bookmark={bookmark}
                  onToggleFavorite={handleToggleFavorite}
                  onEdit={handleEditItem} // SortableBookmarkItem expects 'onEdit'
                  onDelete={handleDeleteItem}
                />
              ))
            ) : (
              <div className="text-center text-zinc-500 py-6">
                No bookmarks found matching your criteria.
              </div>
            )}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};


const BookmarkManager = ({
  isOpen,
  onClose,
  userId, // Keep userId if needed for context, but not for fetching
  bookmarks, // Receive bookmarks from parent
  onToggleFavorite, // Receive handler from parent
  onEdit, // Receive handler from parent for saving edits
  onDelete, // Receive handler from parent
  onReorder, // Receive handler for drag-and-drop reorder
  isLoading // Receive loading state from parent
}) => {
  // Remove internal state for bookmarks and the initial loading state derived from fetch
  // const [bookmarks, setBookmarks] = useState([]);
  // const [isLoading, setIsLoading] = useState(false); // Now passed as prop

  const [searchTerm, setSearchTerm] = useState('');
  const [securityFilter, setSecurityFilter] = useState('all'); // 'all', 'private', 'company', 'community'
  const [isEditModalOpen, setIsEditModalOpen] = useState(false); // State for edit modal visibility
  const [editingBookmark, setEditingBookmark] = useState(null); // State to hold bookmark being edited
  const [activeTab, setActiveTab] = useState("manageFavorites"); // Added state for active tab

  // Local state to manage the visual order during drag-and-drop
  // Initialize safely, checking if bookmarks prop is available
  const [orderedBookmarkIds, setOrderedBookmarkIds] = useState(() => bookmarks ? bookmarks.map(b => b.id) : []);

  // Remove the useEffect hook that fetches data using onSnapshot
  // useEffect(() => { ... Firebase listener logic ... }, [userId]);

  // Update local order state when bookmarks prop changes externally (e.g., after fetch or deletion)
  // Also handle cases where bookmarks might become undefined/null temporarily
  useEffect(() => {
    setOrderedBookmarkIds(bookmarks ? bookmarks.map(b => b.id) : []);
  }, [bookmarks]);


  // --- Filtering Logic ---
  const filteredBookmarks = useMemo(() => {
    // Keep track of the order based on the orderedBookmarkIds state
    const orderedMap = new Map(orderedBookmarkIds.map((id, index) => [id, index]));

    // Ensure bookmarks is an array before attempting to filter/sort
    if (!Array.isArray(bookmarks)) {
      return [];
    }

    return bookmarks
      .filter(bookmark => {
        // Handle potential missing 'name' field
        const name = bookmark.name || ''; // Default to empty string if name is missing/nullish
        const matchesSearch = searchTerm === '' || name.toLowerCase().includes(searchTerm.toLowerCase());

        // Check availability fields based on Firestore structure
        const availability = bookmark.availability || {}; // Default to empty object if availability is missing
        let matchesSecurity = false;
        switch (securityFilter) {
          case 'private':
            matchesSecurity = availability.private === true;
            break;
          case 'company':
            matchesSecurity = availability.company === true;
            break;
          case 'community':
            matchesSecurity = availability.community === true;
            break;
          case 'all':
          default:
            matchesSecurity = true;
            break;
        }

        return matchesSearch && matchesSecurity;
      })
      // Sort based on the current local drag-and-drop order
      .sort((a, b) => (orderedMap.get(a.id) ?? Infinity) - (orderedMap.get(b.id) ?? Infinity));
  }, [bookmarks, searchTerm, securityFilter, orderedBookmarkIds]);


  // Map IDs for SortableContext
  const filteredBookmarkIds = useMemo(() => filteredBookmarks.map(b => b.id), [filteredBookmarks]);

  // --- Action Handlers (Call props passed from parent) ---
  const handleToggleFavorite = useCallback((id, isFavorite) => {
    // Directly call the parent handler
    onToggleFavorite(id, isFavorite);
  }, [onToggleFavorite]);


  // Opens the edit modal with the selected bookmark's data (Local state change)
  const handleLocalEdit = useCallback((bookmark) => {
    console.log("Opening edit modal for:", bookmark);
    setEditingBookmark(bookmark);
    setIsEditModalOpen(true);
  }, []);

  // Closes the edit modal and clears the editing state (Local state change)
  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingBookmark(null);
  }, []);

  // Saves the edited bookmark data by calling the parent's save handler
  const handleSaveEdit = useCallback(async (updatedData) => {
    if (!editingBookmark || !onEdit) {
      console.error("Cannot save edit: missing bookmark data or onEdit handler prop.");
      return;
    }

    console.log("Calling parent onEdit to save bookmark:", editingBookmark.id, updatedData);

    try {
      // Call the parent handler (passed as 'onEdit' prop) to perform the save
      await onEdit(editingBookmark.id, updatedData);
      console.log("Parent handler finished saving.");
      handleCloseEditModal(); // Close modal on success
    } catch (error) {
      console.error("Error saving bookmark via parent handler: ", error);
      // TODO: Show error feedback to the user
      alert("Error updating bookmark. Please try again."); // Simple error feedback
    }
  }, [editingBookmark, onEdit, handleCloseEditModal]);


  const handleDelete = useCallback((id) => {
    // Consider moving confirmation to parent if needed, or keep it here
    if (window.confirm("Are you sure you want to delete this bookmark?")) {
      console.log("Calling parent onDelete for:", id);
      onDelete(id); // Call parent handler
    }
  }, [onDelete]); // Depend on parent handler


  // --- Drag and Drop ---
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) { // Check if 'over' exists and is different
      const oldIndex = orderedBookmarkIds.indexOf(active.id);
      const newIndex = orderedBookmarkIds.indexOf(over.id);

      // Ensure both items involved in the drag are found in the current local order
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrderIds = arrayMove(orderedBookmarkIds, oldIndex, newIndex);

        // Optimistically update the local order state for immediate visual feedback
        setOrderedBookmarkIds(newOrderIds);

        // Call the parent handler to persist the new order
        console.log("Calling parent onReorder with new order:", newOrderIds);
        onReorder(newOrderIds);
      } else {
        console.warn("Drag end involved an item not found in the current order state. This might happen if the list was updated during drag.", { activeId: active.id, overId: over.id, currentIds: orderedBookmarkIds });
      }
    }
  }, [orderedBookmarkIds, onReorder]); // Depend on local order state and parent handler

  // --- Community Specific Filtering ---
  const communityFilteredBookmarks = useMemo(() => {
    return filteredBookmarks.filter(bookmark => bookmark.availability?.community === true);
  }, [filteredBookmarks]);

  const communityFilteredBookmarkIds = useMemo(() => communityFilteredBookmarks.map(b => b.id), [communityFilteredBookmarks]);

  // --- Company Specific Filtering ---
  const companyFilteredBookmarks = useMemo(() => {
    return filteredBookmarks.filter(bookmark => bookmark.availability?.company === true);
  }, [filteredBookmarks]);

  const companyFilteredBookmarkIds = useMemo(() => companyFilteredBookmarks.map(b => b.id), [companyFilteredBookmarks]);


  // --- Tab Data ---
  const tabData = [
    {
      label: "My Apps",
      value: "manageFavorites",
    },
    {
      label: "Community",
      value: "community",
    },
    // {
    //   label: "Company",
    //   value: "myCompany",
    // },
  ];

  // --- Render ---
  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose} // Close on backdrop click
          >
            <motion.div
              className="bg-zinc-900 rounded-lg   shadow-xl  w-full max-w-7xl max-h-[90vh] flex flex-col" // Adjusted width & max-width
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ type: "spring", damping: 25, stiffness: 500 }}
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
            >
              {/* Header */}


              {/* Tabs */}
              <Tabs value={activeTab} className="flex-grow  flex flex-col overflow-hidden">
                <TabsHeader
                  className="rounded-none bg-blaxck/25 p-0 px-4 pt-4 mb-4 gap-x-4 "
                  indicatorProps={{
                    className: "bg-transparent border-b-2 border-zinc-100 shadow-none rounded-none",
                  }}
                >
                  <div className='flex flex-row justify-between items-center sp-big-heading text-3xl ml-4 mr-6'>
                    Apps
                  </div>
                  {tabData.map(({ label, value }, index) => (
                    <Tab
                      key={value}
                      value={value}
                      onClick={() => setActiveTab(value)}
                      className={`py-3 pr-4 text-lg transition-colors duration-200   flex justify-center
                        ${activeTab === value
                          ? "text-zinc-100"
                          : "text-zinc-400 hover:text-zinc-100"
                        }
                        focus:outline-none
                        ${index === tabData.length - 1 ? "" : ""}
                        ${index === 0 ? " ml-4" : ""}
                      `}
                    >
                      {label}
                    </Tab>
                  ))}

                  <button onClick={onClose} className="  text-zinc-500 hover:text-zinc-300">
                    <X size={20} />
                  </button>
                </TabsHeader>
                <TabsBody className="flex-grow  overflow-y-auto min-h-[48vh]">
                  <TabPanel key="manageFavorites" value="manageFavorites" className="p-0 flex-grow flex px-4 flex-col">
                    <div className='w-full flex-grow flex flex-col' id="bookmark-manager-container"> {/* Ensure this container can grow */}
                      {/* Filters and Search */}
                      <div className="p-4 space-y-3 md:space-y-0 md:flex md:items-center md:space-x-3 "> {/* Added border */}
                        <div className="relative flex-grow">
                          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-500" />
                          <input
                            type="text"
                            placeholder="Search favorites..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-zinc-800 border border-zinc-700 rounded-md pl-10 pr-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500" // Adjusted padding
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-zinc-400 flex-shrink-0">Filter:</span>
                          <select
                            value={securityFilter}
                            onChange={(e) => setSecurityFilter(e.target.value)}
                            className="bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500" // Adjusted padding
                          >
                            <option value="all">All</option>
                            <option value="private">Private</option>
                            <option value="company">Company</option>
                            <option value="community">Community</option>
                          </select>
                        </div>
                      </div>

                      {/* Bookmark List */}
                      <BookmarkListDisplay
                        isLoading={isLoading}
                        sensors={sensors}
                        handleDragEnd={handleDragEnd}
                        filteredBookmarkIds={filteredBookmarkIds}
                        filteredBookmarks={filteredBookmarks}
                        handleToggleFavorite={handleToggleFavorite}
                        handleEditItem={handleLocalEdit} // Pass handleLocalEdit to the new component
                        handleDeleteItem={handleDelete}   // Pass handleDelete to the new component
                      />
                    </div>
                  </TabPanel>
                  <TabPanel key="community" value="community" className="p-0 flex-grow px-4  flex flex-col">
                    <BookmarkListDisplay
                      isLoading={isLoading}
                      sensors={sensors}
                      handleDragEnd={handleDragEnd}
                      filteredBookmarkIds={communityFilteredBookmarkIds}
                      filteredBookmarks={communityFilteredBookmarks}
                      handleToggleFavorite={handleToggleFavorite}
                      handleEditItem={handleLocalEdit} // Pass handleLocalEdit to the new component
                      handleDeleteItem={handleDelete}   // Pass handleDelete to the new component
                    />
                  </TabPanel>
                  <TabPanel key="myCompany" value="myCompany" className="p-0 flex-grow px-4  flex flex-col">
                    <BookmarkListDisplay
                      isLoading={isLoading}
                      sensors={sensors}
                      handleDragEnd={handleDragEnd}
                      filteredBookmarkIds={companyFilteredBookmarkIds}
                      filteredBookmarks={companyFilteredBookmarks}
                      handleToggleFavorite={handleToggleFavorite}
                      handleEditItem={handleLocalEdit}
                      handleDeleteItem={handleDelete}
                    />
                  </TabPanel>
                </TabsBody>
              </Tabs>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Bookmark Modal */}
      {editingBookmark && (
        <Bookmark // Ensure Bookmark component can handle initialData and calls onSave
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSave={handleSaveEdit} // Pass down the save handler connected to the parent
          initialContent={editingBookmark}
        // Pass userId or other context if Bookmark component needs it
        />
      )}
    </>
  );
};

export default BookmarkManager;
